import React from 'react';
import { API } from '../api';

/*
 * A club's logos, and which one it actually uses.
 *
 * This is the only place a logo is added. They are kept in the club's
 * gallery, alongside its photographs, and are visible there — but a logo is
 * not just another picture: it is what goes in the header, on the club
 * screen, on a member card and into the corner of every poster. Choosing one
 * belongs with the club's colours, not in a grid of photographs.
 *
 * The default is copied into the club's `logo` field as well as pointed at,
 * because every screen in the app already reads that field. So the gallery
 * holds the set and `logo` is the answer.
 *
 * A PNG with a transparent background sits best on a poster, so logos are
 * stored exactly as uploaded rather than re-encoded: flattening one to JPEG
 * would put a white box behind the mark on everything it is placed on.
 */

const MAX_EDGE = 400; // stored, not displayed: big enough to composite onto a 4K poster

/** Shrink on the way in, keeping PNG so transparency survives. */
const readAndShrink = (file, onDone, onError) => {
  const reader = new FileReader();
  reader.onload = ev => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height));
      const w = Math.round(image.width * scale);
      const h = Math.round(image.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(image, 0, 0, w, h);
      onDone(canvas.toDataURL('image/png'));
    };
    image.onerror = () => onError('That file could not be read as an image.');
    image.src = ev.target.result;
  };
  reader.onerror = () => onError('That file could not be read.');
  reader.readAsDataURL(file);
};

function ClubLogoSet({ club, clubEditForm, setClubEditForm, showToast }) {
  const inputRef = React.useRef(null);
  const [logos, setLogos] = React.useState([]);
  const [defaultId, setDefaultId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  /* The id comes from the club, not from the edit form: the form holds the
     fields being edited and has never carried an id. Reading it from there
     produced a gallery request with no club on it, which quietly returned
     the wrong club's logos or none at all. */
  const clubId = club && club.id;

  const load = React.useCallback(async () => {
    const r = await fetch(`${API}/api/gallery?logos=1${clubId ? '&clubId=' + clubId : ''}`);
    if (!r.ok) return;
    const data = await r.json();
    setLogos(data.photos);
  }, [clubId]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const chosen = (clubEditForm && clubEditForm.logoPhotoId) || (club && club.logoPhotoId);
    setDefaultId(chosen ? String(chosen) : null);
  }, [club, clubEditForm]);

  const add = async files => {
    const file = files && files[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) { showToast('That is not an image', 'red'); return; }
    if (file.size > 5 * 1024 * 1024) { showToast('Image must be under 5 MB', 'red'); return; }

    setBusy(true);
    readAndShrink(file, async dataUrl => {
      try {
        const r = await fetch(`${API}/api/gallery`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clubId, data: dataUrl, isLogo: true,
            label: file.name.replace(/\.[^.]+$/, '').slice(0, 40) || 'Logo',
          }),
        });
        const out = await r.json().catch(() => ({}));
        if (!r.ok) { showToast(out.error || 'That logo would not go in', 'red'); return; }
        await load();
        // The first one uploaded is the default; there is nothing to choose between.
        if (!logos.length && out.photo) await makeDefault(out.photo);
        showToast('Logo added', 'green');
      } finally { setBusy(false); }
    }, msg => { setBusy(false); showToast(msg, 'red'); });
  };

  const makeDefault = async l => {
    const r = await fetch(`${API}/api/gallery/default-logo`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clubId, photoId: l.id }),
    });
    if (!r.ok) return showToast('That could not be set as the default', 'red');
    setDefaultId(String(l.id));
    /* Only which one, never the image. The endpoint above has already copied
       the full-resolution file into club.logo; putting the thumbnail into the
       form as well meant saving the club afterwards wrote the small one over
       the top of it. */
    setClubEditForm(f => ({ ...f, logoPhotoId: l.id }));
    return showToast('Default logo set', 'green');
  };

  const rename = async (l, label) => {
    setLogos(list => list.map(x => (x.id === l.id ? { ...x, label } : x)));
    await fetch(`${API}/api/gallery/${l.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    });
  };

  const remove = async l => {
    const r = await fetch(`${API}/api/gallery/${l.id}`, { method: 'DELETE' });
    if (!r.ok) return showToast('It could not be removed', 'red');
    const left = logos.filter(x => x.id !== l.id);
    await load();
    /* If the default went with it, promote whatever is left. A club with
       logos and no default shows no logo anywhere, which is worse than
       choosing for them. */
    if (String(defaultId) === String(l.id)) {
      if (left[0]) await makeDefault(left[0]);
      else setClubEditForm(f => ({ ...f, logoPhotoId: null }));
    }
    return showToast('Logo removed', 'green');
  };

  return (
    <div style={{ marginBottom: 'var(--gs-space-4)' }}>
      <label className="w3-tiny w3-text-grey">
        <i className="fa fa-image w3-margin-right" />Club logos
      </label>
      <p style={{ margin: '4px 0 10px', fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
        The default is the one shown in the header, on the club screen and on member cards.
        A PNG with a transparent background sits best on a poster. Logos are kept in the
        club gallery and can be seen there, but this is where they are added.
      </p>

      <input ref={inputRef} type="file" accept="image/*" hidden aria-hidden="true" tabIndex={-1}
        onChange={e => add(e.target.files)} />

      <div className="logo-set">
        {logos.map(l => {
          const isDefault = String(defaultId) === String(l.id);
          return (
            <div key={l.id} className={`logo-slot${isDefault ? ' logo-slot-default' : ''}`}>
              <div className="logo-slot-img">
                <img src={l.thumb} alt={l.label} />
              </div>
              <input className="logo-slot-name" value={l.label} aria-label="Logo name"
                onChange={e => setLogos(list =>
                  list.map(x => (x.id === l.id ? { ...x, label: e.target.value.slice(0, 40) } : x)))}
                onBlur={e => rename(l, e.target.value.slice(0, 40))} />
              {isDefault ? (
                <span className="logo-slot-badge">Default</span>
              ) : (
                <button type="button" className="logo-slot-action" onClick={() => makeDefault(l)}>
                  Use as default
                </button>
              )}
              <button type="button" className="logo-slot-remove" onClick={() => remove(l)}
                aria-label={`Remove ${l.label}`}>
                Remove
              </button>
            </div>
          );
        })}

        <button type="button" className="logo-slot logo-slot-add" disabled={busy}
          onClick={() => inputRef.current && inputRef.current.click()}>
          <span aria-hidden="true">+</span>
          <span>{busy ? 'Adding…' : 'Add a logo'}</span>
        </button>
      </div>
    </div>
  );
}

export default ClubLogoSet;
